import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { differenceInCalendarDays } from 'date-fns';
import { Fragment, useRef, useState, useEffect, ReactNode, MouseEvent } from 'react';

import { safeFromUnixTime } from '@/utils/dates';
import { safelyRender } from '@/utils/safelyRender';

import { ActionButtons } from '../ActionButtons/ActionButtons';
import { Folder } from '../Folders/types/Folder';
import { ClosedConversationPrompt } from './components/ClosedConversationPrompt';
import { Message } from './components/Message/Message';
import { RichMessageEditor } from './components/RichMessageEditor/RichMessageEditor';
import { MessageType } from './types/Message';

export type MessengerProps = {
    /** Index of the currently active folder in `folders` */
    readonly activeFolder: number | null;
    /** Copy for "Archive" button */
    readonly archiveLabel: ReactNode;
    /** Whether catalog is online or live */
    readonly catalogIsOnlineOrLive?: boolean;
    /** List of folders for the current user */
    readonly folders: Folder[];
    /** Name of auction house involved in conversations */
    readonly houseName: string;
    /** Copy for "Mark Read" button */
    readonly isHouse?: boolean;
    readonly markReadLabel: ReactNode;
    /** Copy for "Mark Unread" button */
    readonly markUnreadLabel: ReactNode;
    /** List of messages in this conversation */
    readonly messages: MessageType[];
    /** Copy for "Move" button */
    readonly moveLabel: ReactNode;
    /** Function to add attachment(s) to a draft, a draft will be created if it does not exist */
    readonly onAddAttachment: (message: string, attachedFiles: any[]) => void;
    /** Callback function for archiving conversation */
    readonly onArchive: () => void;
    /** Callback function when clicking the "back to inbox" button */
    readonly onBack: (event: MouseEvent<HTMLButtonElement | HTMLDivElement>) => void;
    /** Function to delete attachment from message draft */
    readonly onDeleteAttachment: (attachmentName: string) => void;
    /** Callback function to mark a conversation as read */
    readonly onMarkRead: () => void;
    /** Callback function to mark a conversation as unread */
    readonly onMarkUnread: () => void;
    /** Callback function to move a conversation to a different folder */
    readonly onMove: (folder: Folder) => void;
    /** Callback function to send a message */
    readonly onSendMessage: (message: string) => void;
    /** Subject line of conversation */
    readonly subject: string;
    readonly testid?: string;
};

export const Messenger = ({
    activeFolder,
    archiveLabel,
    catalogIsOnlineOrLive,
    folders = [],
    houseName,
    isHouse,
    markReadLabel,
    markUnreadLabel,
    messages,
    moveLabel,
    onAddAttachment,
    onArchive,
    onBack,
    onDeleteAttachment,
    onMarkRead,
    onMarkUnread,
    onMove,
    onSendMessage,
    subject,
    testid = 'messenger',
}: MessengerProps) => {
    const messageListRef = useRef<HTMLUListElement>(null);
    const [readStatus, setReadStatus] = useState<'neither' | 'read' | 'unread' | 'both'>('unread');

    const onMarkReadToggle = () => {
        setReadStatus('unread');
        onMarkRead();
    };

    const onMarkUnreadToggle = () => {
        setReadStatus('read');
        onMarkUnread();
    };

    // Scroll to bottom of message list when number of messages changes
    useEffect(() => {
        if (messageListRef.current) {
            const { clientHeight, scrollHeight } = messageListRef.current;
            const maxScrollTop = scrollHeight - clientHeight;

            messageListRef.current.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
        }
    }, [messages.length]);

    const latestMessageSent = Math.max.apply(
        Math,
        messages.map((m: MessageType) => m.tsSent)
    );
    const threadOlderThan30Days = differenceInCalendarDays(new Date(), safeFromUnixTime(latestMessageSent)) > 30;
    const showClosedConversation = threadOlderThan30Days && !catalogIsOnlineOrLive;

    return (
        <div
            className="flex w-full flex-col justify-start"
            data-testid={testid}
        >
            <ActionButtons
                activeFolder={activeFolder}
                archiveLabel={archiveLabel}
                back={onBack}
                className="mt-[1px]"
                enabled
                folders={folders}
                isRead={readStatus}
                markReadLabel={markReadLabel}
                markUnreadLabel={markUnreadLabel}
                moveLabel={moveLabel}
                onArchive={onArchive}
                onMarkRead={onMarkReadToggle}
                onMarkUnread={onMarkUnreadToggle}
                onMove={onMove}
            />
            <div className="mt-[15px] border-t border-border-secondary pb-sm pl-[53px] pt-[12px]">
                <H5 className="font-normal">{safelyRender({ html: subject, wrapper: 'span' })}</H5>
            </div>
            <ul
                className="m-0 flex list-none flex-col overflow-y-auto p-0 pb-[20px]"
                ref={messageListRef}
            >
                {messages.map((currMessage: MessageType) => (
                    <Fragment key={currMessage.messageId}>
                        <Message message={currMessage} />
                    </Fragment>
                ))}
            </ul>
            <div className="print:hidden">
                {showClosedConversation ? (
                    <ClosedConversationPrompt
                        houseName={houseName}
                        isHouse={isHouse}
                        onBack={onBack}
                    />
                ) : (
                    <RichMessageEditor
                        onAddAttachment={onAddAttachment}
                        onDeleteAttachment={onDeleteAttachment}
                        onSendMessage={onSendMessage}
                    />
                )}
            </div>
        </div>
    );
};
